import { BarChart3, Building2, Calendar, LogOut, Menu, Settings, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../api/index';
import { queryClient } from '../App';
import { useAuthStore } from '../store/auth';

interface SuperAdminLayoutProps {
  children: React.ReactNode;
}

export default function SuperAdminLayout({ children }: SuperAdminLayoutProps) {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    // Redirect if not superadmin
    if (user && user.role !== 'superadmin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  if (!user || user.role !== 'superadmin') {
    return null;
  }

  const menuItems = [
    {
      label: 'Dashboard',
      icon: Building2,
      href: '/superadmin/dashboard',
    },
    {
      label: 'Analytics',
      icon: BarChart3,
      href: '/superadmin/analytics',
    },
    {
      label: 'Subscriptions',
      icon: Calendar,
      href: '/superadmin/subscriptions',
    },
    {
      label: 'Settings',
      icon: Settings,
      href: '/superadmin/settings',
    },
  ];

  const handleLogout = async () => {
    try {
      // Logout and clear auth cookies (refresh token is HttpOnly, backend will read from cookie)
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear auth state
    clear();
    // Clear all cached queries
    queryClient.clear();
    // Close sidebar if open
    setSidebarOpen(false);
    toast.success('Logged out successfully');
    // Navigate to login
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-lg transition-transform duration-300 ease-in-out md:translate-x-0 md:relative`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <Zap className="h-8 w-8 text-blue-600" />
            <div>
              <div className="font-bold text-gray-900">RTO Admin</div>
              <div className="text-xs font-semibold uppercase text-blue-600">SuperAdmin</div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="mt-8 px-4 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                className="flex items-center gap-3 rounded-lg px-4 py-2 text-gray-700 transition-colors hover:bg-blue-50 hover:text-blue-600"
                onClick={() => setSidebarOpen(false)}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 p-4">
          <div className="mb-4 divide-y divide-gray-200 rounded-lg bg-gray-50 p-3">
            <div className="pb-2 text-sm font-semibold text-gray-700">{user.full_name}</div>
            <div className="pt-2 text-xs text-gray-600">{user.email}</div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-100"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4 shadow-sm">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100"
          >
            <Menu className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Super Admin Portal</h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">Welcome, {user.full_name}</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
